import React, { useState, useEffect, useRef } from "react";
import { Alert, Spinner, Modal, Button } from "react-bootstrap";
import dayjs from "dayjs";
import RIcon from "../../reservas/components/ReservasIcons";
import {
  SPORT_LABELS,
  SURFACE_LABELS,
  ENCLOSURE_LABELS,
  DURATION_LABELS,
} from "../../../constants/courtDomain";

const formatPrice = (price) =>
  typeof price === "number" && price > 0 ? `$${price.toLocaleString("es-AR")}` : "Consultar";

const getSportIconName = (sport) => {
  if (sport === "padel" || sport === "tennis") return "court";
  return "ball";
};

const formatDateLabel = (date) => {
  if (!date) return "";
  const today = dayjs().startOf("day");
  if (date.isSame(today, "day")) return `Hoy ${date.format("DD/MM")}`;
  if (date.isSame(today.add(1, "day"), "day")) return `Mañana ${date.format("DD/MM")}`;
  return date.format("dddd DD/MM");
};

/**
 * Presentational & Interactive: Renders the ATC Sports-style availability grid.
 * Rows: Courts
 * Columns: Hour slots (dynamic range from real data)
 * Clicking an available cell opens a popover (desktop) or modal (mobile) to select duration & price,
 * and clicking "Listo" selects the turn and updates the size of the green selection bar.
 */
export default function CourtSlotSelector({
  courts,
  sports = [],
  selectedSport,
  setSelectedSport,
  selectedDate,
  setSelectedDate,
  selectedDuration,
  setSelectedDuration,
  selectedCourtId,
  selectedStart,
  onSelectSlot,
  loading,
  error,
  minDate,
  maxDate,
  reservationWindowDays,
  isMobile = false,
}) {
  const cardRef = useRef(null);
  const wrapperRef = useRef(null);
  const popoverRef = useRef(null);

  const [activeCell, setActiveCell] = useState(null);
  const [selectedOptIdx, setSelectedOptIdx] = useState(0);

  // Close popover on clicking outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        !isMobile &&
        activeCell &&
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        !e.target.closest(".court-grid-cell.available")
      ) {
        setActiveCell(null);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [activeCell, isMobile]);

  // Close popover when horizontal grid scrolls
  useEffect(() => {
    if (isMobile || !activeCell) return;
    const handleScroll = () => {
      setActiveCell(null);
    };
    const wrapper = wrapperRef.current;
    if (wrapper) {
      wrapper.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (wrapper) wrapper.removeEventListener("scroll", handleScroll);
    };
  }, [activeCell, isMobile]);

  // Clean popover when date or sport changes
  useEffect(() => {
    setActiveCell(null);
  }, [selectedDate, selectedSport]);

  if (loading) {
    return (
      <div className="court-slot-selector court-slot-loading" style={{ minHeight: "350px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spinner animation="border" style={{ color: "var(--zoco-lime-strong)" }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: "350px", display: "flex", alignItems: "center" }}>
        <Alert variant="danger" className="w-100">{error}</Alert>
      </div>
    );
  }

  // Filter courts by sport
  const filteredCourts = selectedSport
    ? courts.filter((c) => c.sport === selectedSport)
    : courts;

  // Determine time axis columns dynamically based on granularity and slot hours
  const granularity = 30; // default interval
  let minHour = 24;
  let maxHour = 0;
  let hasSlots = false;

  filteredCourts.forEach((court) => {
    if (court.bookableStarts) {
      const slotsList = Array.isArray(court.bookableStarts)
        ? [court.bookableStarts]
        : Object.values(court.bookableStarts);

      slotsList.forEach((slots) => {
        if (Array.isArray(slots)) {
          slots.forEach((s) => {
            hasSlots = true;
            const hStart = parseInt(s.slotStart.split(":")[0], 10);
            const hEnd = parseInt(s.slotEnd.split(":")[0], 10);
            if (hStart < minHour) minHour = hStart;
            if (hEnd > maxHour) maxHour = hEnd;
          });
        }
      });
    }
  });

  if (!hasSlots) {
    minHour = 8;
    maxHour = 22;
  } else {
    minHour = Math.max(0, minHour - 1);
    maxHour = Math.min(24, maxHour + 1);
  }

  const timeColumns = [];
  for (let h = minHour; h <= maxHour; h++) {
    timeColumns.push(`${String(h).padStart(2, "0")}:00`);
    if (granularity === 30 && h < maxHour) {
      timeColumns.push(`${String(h).padStart(2, "0")}:30`);
    }
  }

  // Get duration/price options for a cell
  const getOptionsForCell = (court, timeStr) => {
    const options = [];
    if (!court.bookableStarts) return options;
    const entries = Array.isArray(court.bookableStarts)
      ? [["60", court.bookableStarts]]
      : Object.entries(court.bookableStarts);

    entries.forEach(([durationStr, slots]) => {
      if (Array.isArray(slots)) {
        const slot = slots.find((s) => s.slotStart === timeStr);
        if (slot) {
          options.push({
            duration: parseInt(durationStr, 10),
            ...slot,
          });
        }
      }
    });
    return options.sort((a, b) => a.duration - b.duration);
  };

  const handleCellClick = (e, court, col, options) => {
    if (isMobile) {
      setActiveCell({
        courtId: court.courtId,
        courtName: court.name,
        time: col,
        options,
      });
      const matchIdx = options.findIndex((opt) => opt.duration === selectedDuration);
      setSelectedOptIdx(matchIdx >= 0 ? matchIdx : 0);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const cardRect = cardRef.current.getBoundingClientRect();
      // Position popover relative to the outer court-grid-card container
      const left = rect.left - cardRect.left + rect.width / 2;
      const top = rect.bottom - cardRect.top;

      setActiveCell({
        courtId: court.courtId,
        courtName: court.name,
        time: col,
        options,
        coords: { top, left },
      });

      const matchIdx = options.findIndex((opt) => opt.duration === selectedDuration);
      setSelectedOptIdx(matchIdx >= 0 ? matchIdx : 0);
    }
  };

  const handleDateNav = (direction) => {
    if (!selectedDate) return;
    const nextDate = selectedDate.add(direction, "day");
    if (nextDate.isBefore(minDate, "day") || nextDate.isAfter(maxDate, "day")) return;
    setSelectedDate(nextDate);
  };

  const handleContinueSelection = () => {
    if (!activeCell) return;
    const chosen = activeCell.options[selectedOptIdx];
    onSelectSlot(activeCell.courtId, chosen, chosen.duration);
    setActiveCell(null);
  };

  // Helper to determine if a cell is highlighted green (part of selected block)
  const getHighlightInfo = () => {
    if (activeCell) {
      return {
        courtId: activeCell.courtId,
        startStr: activeCell.time,
        duration: activeCell.options[selectedOptIdx]?.duration || selectedDuration,
      };
    }
    if (selectedCourtId && selectedStart) {
      return {
        courtId: selectedCourtId,
        startStr: selectedStart.slotStart,
        duration: selectedDuration,
      };
    }
    return null;
  };

  const highlight = getHighlightInfo();

  const isCellHighlighted = (courtId, col) => {
    if (!highlight || highlight.courtId !== courtId) return false;

    const [colH, colM] = col.split(":").map(Number);
    const colMinutes = colH * 60 + colM;

    const [startH, startM] = highlight.startStr.split(":").map(Number);
    const startMinutes = startH * 60 + startM;

    const endMinutes = startMinutes + highlight.duration;

    return colMinutes >= startMinutes && colMinutes < endMinutes;
  };

  return (
    <div className={isMobile ? "court-grid-card-mobile" : "court-grid-card"} ref={cardRef}>
      {/* Grid Header Card */}
      <div className={isMobile ? "court-grid-header-mobile" : "court-grid-header"}>
        {/* Sport dropdown filter */}
        <div className="court-grid-sport-select-wrapper">
          {sports.map((s) => (
            <button
              type="button"
              key={s}
              className={isMobile ? `court-grid-sport-btn-mobile${selectedSport === s ? " active" : ""}` : `court-grid-sport-btn${selectedSport === s ? " active" : ""}`}
              onClick={() => setSelectedSport(s)}
            >
              <RIcon name={getSportIconName(s)} width={14} height={14} />
              {SPORT_LABELS[s] || s}
            </button>
          ))}
        </div>

        {/* Date Navigation slider */}
        <div className={isMobile ? "court-grid-date-nav-mobile" : "court-grid-date-nav"}>
          <button
            type="button"
            className={isMobile ? "court-grid-nav-btn-mobile" : "court-grid-nav-btn"}
            onClick={() => handleDateNav(-1)}
            disabled={!selectedDate || (minDate && selectedDate.isSame(minDate, "day"))}
            style={{ opacity: !selectedDate || (minDate && selectedDate.isSame(minDate, "day")) ? 0.35 : 1 }}
          >
            <RIcon name="chevL" width={14} height={14} />
          </button>
          <span className={isMobile ? "court-grid-date-title-mobile" : "court-grid-date-title"}>
            {selectedDate ? formatDateLabel(selectedDate) : ""}
          </span>
          <button
            type="button"
            className={isMobile ? "court-grid-nav-btn-mobile" : "court-grid-nav-btn"}
            onClick={() => handleDateNav(1)}
            disabled={!selectedDate || (maxDate && selectedDate.isSame(maxDate, "day"))}
            style={{ opacity: !selectedDate || (maxDate && selectedDate.isSame(maxDate, "day")) ? 0.35 : 1 }}
          >
            <RIcon name="chevR" width={14} height={14} />
          </button>
        </div>
      </div>

      {/* Grid Table Container */}
      <div className="court-grid-table-wrapper" ref={wrapperRef}>
        {filteredCourts.length === 0 ? (
          <div style={{ padding: "40px 20px", textAlign: "center", fontSize: "14px", color: "#6b7280" }}>
            No hay canchas disponibles para este deporte en la fecha seleccionada.
          </div>
        ) : (
          <table className="court-grid-table">
            <thead>
              <tr>
                <th className="court-grid-corner-cell">Cancha</th>
                {timeColumns.map((col) => {
                  const isHour = col.endsWith(":00");
                  const hourText = col.split(":")[0];
                  return (
                    <th key={col} className="court-grid-header-cell">
                      {isHour ? (
                        <span className="hour-label">{hourText}</span>
                      ) : (
                        <span className="hour-sub-label" />
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filteredCourts.map((court) => (
                <tr key={court.courtId}>
                  <td className="court-grid-name-cell">
                    <div className="court-name-info">
                      <span className="name">{court.name}</span>
                      <span className="meta">
                        {[
                          SURFACE_LABELS[court.surface] || court.surface,
                          ENCLOSURE_LABELS[court.enclosure] || court.enclosure,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </div>
                  </td>
                  {timeColumns.map((col) => {
                    const options = getOptionsForCell(court, col);
                    const isAvailable = options.length > 0;
                    const isHighlighted = isCellHighlighted(court.courtId, col);

                    return (
                      <td
                        key={col}
                        className={`court-grid-cell${isAvailable ? " available" : " unavailable"}${isHighlighted ? " selected" : ""}`}
                        onClick={(e) => isAvailable && handleCellClick(e, court, col, options)}
                      />
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Grid Legend Footer */}
      <div className="court-grid-legend">
        <span>
          Las reservas se pueden realizar hasta con {reservationWindowDays || 6} días de antelación
        </span>
        <div className="court-grid-legend-items">
          <div className="court-grid-legend-item">
            <div className="court-grid-legend-color available" />
            <span>Disponible</span>
          </div>
          <div className="court-grid-legend-item">
            <div className="court-grid-legend-color unavailable" />
            <span>No disponible</span>
          </div>
          <div className="court-grid-legend-item">
            <div className="court-grid-legend-color selected" />
            <span>Tu reserva</span>
          </div>
        </div>
      </div>

      {/* Desktop Popover selector (Rendered OUTSIDE wrapper to overlap freely) */}
      {!isMobile && activeCell && activeCell.coords && (
        <div
          className="court-grid-popover"
          ref={popoverRef}
          style={{
            top: `${activeCell.coords.top}px`,
            left: `${activeCell.coords.left}px`,
          }}
        >
          <div className="popover-arrow" />
          <div className="court-grid-popover-header">
            <span className="court-title">{activeCell.courtName}</span>
            <span className="time-title">
              <RIcon name="clock" width={13} height={13} /> {activeCell.time} hs
            </span>
          </div>
          <div className="duration-options">
            {activeCell.options.map((opt, idx) => (
              <button
                type="button"
                key={opt.duration}
                className={`duration-opt-btn${selectedOptIdx === idx ? " active" : ""}`}
                onClick={() => setSelectedOptIdx(idx)}
              >
                <span className="dur">{opt.duration} min</span>
                <span className="pr">{formatPrice(opt.price)}</span>
              </button>
            ))}
          </div>
          <button
            type="button"
            className="r-btn-primary popover-cta"
            onClick={handleContinueSelection}
          >
            Listo
          </button>
        </div>
      )}

      {/* Mobile Modal selector */}
      {isMobile && activeCell && (
        <Modal
          show={true}
          onHide={() => setActiveCell(null)}
          centered
          className="court-mobile-modal"
          backdrop="static"
        >
          <Modal.Header closeButton>
            <Modal.Title>Elegí la duración del turno</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div style={{ marginBottom: "16px", fontSize: "14px", color: "#4b5563" }}>
              Cancha: <strong>{activeCell.courtName}</strong> a las <strong>{activeCell.time} hs</strong>
            </div>
            <div className="duration-options">
              {activeCell.options.map((opt, idx) => (
                <button
                  type="button"
                  key={opt.duration}
                  className={`duration-opt-btn${selectedOptIdx === idx ? " active" : ""}`}
                  onClick={() => setSelectedOptIdx(idx)}
                >
                  <span className="dur">{opt.duration} min</span>
                  <span className="pr">{formatPrice(opt.price)}</span>
                </button>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setActiveCell(null)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              className="popover-cta"
              onClick={handleContinueSelection}
            >
              Listo
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}
